// EstimatePanel - "what would this view cost to model and coordinate"
// (guide 21.2), shown on a technical view's page.
//
//   <EstimatePanel projectId={projectId} view={view} />
//   view: { viewId, version, title? }  - the version on screen
//
// The quantities are the view's own rows, mapped to production keys on the
// server (utils/estimate/fromView), so the panel, the Estimates page and the
// PDF count the same things. Rows with no production key are listed as not
// priced, never dropped. Prices are the owner company's: for anyone else
// the server refuses, and the panel renders nothing.
import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Space, Table, Tag, Tooltip, message } from "antd";
import { Link } from "react-router-dom";
import { api, hrs, PreviewPanel } from "pages/Estimates/estimateShared";
import GlossaryTerm from "./GlossaryTerm";
import EvidenceThumb from "./EvidenceThumb";

// The two options every estimate carries (EstimatesPage uses the same).
const OPTIONS = [
  { key: "model", label: "Modeling only", buckets: ["modeling"] },
  { key: "full", label: "Modeling and coordination", buckets: ["modeling", "coordination", "oversight"] },
];

export default function EstimatePanel({ projectId, view }) {
  const viewId = view && view.viewId;
  const version = view && view.version;
  const [q, setQ] = useState(null);          // quantities from the view
  const [hidden, setHidden] = useState(false); // not the owner company
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!projectId || !viewId) return undefined;
    let alive = true;
    setQ(null); setPreview(null); setHidden(false);
    api(`/projects/${projectId}/estimates/from-view/${encodeURIComponent(viewId)}${version != null ? `?version=${version}` : ""}`)
      // Normalised here, so a partial answer can never break the page this sits on.
      .then((d) => { if (alive) setQ({ ...(d || {}), rows: (d && d.rows) || [], unmapped: (d && d.unmapped) || [], sourceDocs: (d && d.sourceDocs) || [], view: (d && d.view) || { rows: 0 }, levels: (d && d.levels) || 1, counted: (d && d.counted) || 0 }); })
      .catch(() => { if (alive) setHidden(true); });
    return () => { alive = false; };
  }, [projectId, viewId, version]);

  if (hidden || !viewId) return null;

  const runPreview = async () => {
    setBusy(true);
    try {
      const d = await api(`/projects/${projectId}/estimates/preview`, {
        method: "POST",
        body: JSON.stringify({ rows: q.rows, levels: q.levels, options: OPTIONS, coordination: { rounds: 3 } }),
      });
      setPreview(d.results);
    } catch (e) { message.error(e.message); } finally { setBusy(false); }
  };

  const open = `/coordination/${projectId}/estimates?view=${encodeURIComponent(viewId)}${version != null ? `&version=${version}` : ""}`;
  return (
    <Card size="small" style={{ marginTop: 16 }} loading={!q}
          title={<span>Estimate from this view <Tag>{`v${version}`}</Tag></span>}
          extra={(
            <Space size={6}>
              <Tooltip title="Hours and fee from the organisation's rates and rate card; nothing is saved">
                <Button size="small" onClick={runPreview} loading={busy} disabled={!q || !q.rows.length}>Preview hours and fee</Button>
              </Tooltip>
              <Link to={open}><Button size="small" type="primary" disabled={!q || !q.rows.length}>Open in Estimates</Button></Link>
            </Space>
          )}>
      {q ? (
        <>
          {!q.rows.length ? <Alert type="info" showIcon message="Nothing in this view has a production rate (sleeves, EZ-Path kits, riser sleeves, home runs, DAS remotes)." /> : (
            <Table size="small" pagination={false} rowKey={(r) => `${r.rateKey}|${r.system}|${r.level}|${r.confidence}`} dataSource={q.rows} columns={[
              { title: "Item", dataIndex: "label", render: (v, r) => <GlossaryTerm rateKey={r.rateKey}>{v}</GlossaryTerm> },
              { title: "Rate key", dataIndex: "rateKey", width: 160 },
              { title: "Qty", dataIndex: "qtyForRate", width: 80, align: "right" },
              { title: "Confidence", dataIndex: "confidence", width: 120, render: (v) => <Tag>{v}</Tag> },
              { title: "Evidence", key: "ev", width: 90, render: (_, r) => <EvidenceThumb projectId={projectId} evidence={r.evidence} /> },
            ]} />
          )}
          {q.unmapped.length ? (
            <Alert style={{ marginTop: 8 }} type="warning" showIcon
                   message={`Not priced, no production key: ${q.unmapped.map((u) => `${u.qty} x ${u.type}`).join(", ")}`} />
          ) : null}
          <div style={{ marginTop: 8, color: "#666", fontSize: 12 }}>
            {q.counted} counted item(s) over {q.levels} level(s) from {q.view.rows} view row(s)
            {q.sourceDocs.length ? `; ${q.sourceDocs.length} source document(s) - a later revision marks a saved estimate stale` : ""}.
          </div>
          {preview ? (
            <>
              <PreviewPanel r={preview} />
              <div style={{ marginTop: 6, color: "#666", fontSize: 12 }}>
                Coordination assumes 3 rounds here; set rounds, meetings, allowances and alternates in Estimates. Modelling {hrs(preview.hours && preview.hours.modeling)}.
              </div>
            </>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}
